import classNames from "classnames";
import find from "lodash/find";
import noop from "lodash/noop";
import React, { FunctionComponent, MouseEvent, ReactNode } from "react";
import { useDrop } from "react-dnd";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as ImageIcon } from "../../../../icons/image.svg";
import { Panel } from "../../../../states/customization";
import { ContentPage } from "../../../../types/customization/content";
import { Template, TemplatePageDefinition } from "../../../../types/customization/template";
import { calcDropZone } from "../../../../utils/customization/bleed";
import { isLowQualityPicture, sizeMmToPx, sizeP10kToMm } from "../../../../utils/customization/box";
import { drawClippedPath } from "../../../../utils/customization/clipping";
import { hasSelectionInPanel } from "../../../../utils/customization/configuration";
import { findEditablePictureDefinition } from "../../../../utils/customization/finders";
import { findPhoto } from "../../../../utils/selection/finders";
import { imageUrlFromSelectionPhoto } from "../../../../utils/selection/photo";
import EditablePictureSvg from "../../../atoms/customization/EditablePictureSvg";
import LowQualityIndicator from "../../../atoms/customization/LowQualityIndicator";
import Draggable from "../../../Draggable";
import ClippingPath from "../ClippingPath";
import ShapeBorders from "../ShapeBorders";
import "./style.scss";

interface Props {
  elementDefinitionKey: string;
  page: ContentPage;
  pageDefinition: TemplatePageDefinition;
  ppmm: number;
  template: Template;
}

const EditablePicture: FunctionComponent<Props> = ({
  elementDefinitionKey,
  page,
  pageDefinition,
  ppmm,
  template,
}) => {
  const {
    dispatch,
    selection,
    ui: { draggedPhoto, panel },
  } = useCustomizationContext();
  const intl = useIntl();

  const pageKey = page.key;
  const elementDefinition = findEditablePictureDefinition(elementDefinitionKey, pageDefinition);

  let isCandidateToDrop = false;
  if (
    draggedPhoto &&
    !(
      draggedPhoto.from === "editablePicture" &&
      draggedPhoto.pageKey === pageKey &&
      draggedPhoto.editablePictureKey === elementDefinitionKey
    )
  ) {
    isCandidateToDrop = true;
  }

  const openSelectionPanel = (selectSlot = false) => {
    dispatch({
      elementDefinitionKey: selectSlot ? elementDefinition.key : undefined,
      key: pageKey,
      openedTab: "photos",
      panel: pageDefinition.type === "front-cover" ? Panel.EDITCOVER : Panel.EDITPAGE,
      type: "setPanel",
    });
  };

  const onClick = (event: MouseEvent) => {
    event.preventDefault();

    dispatch({
      editedPicture: {
        editablePictureKey: elementDefinitionKey,
        pageKey,
      },
      type: "setEditedPicture",
    });
    event.stopPropagation();
  };

  const onDrop = () => {
    if (isCandidateToDrop) {
      if (draggedPhoto && draggedPhoto.from === "selection") {
        dispatch({
          editablePictureKey: elementDefinitionKey,
          pageKey,
          selectionPhoto: draggedPhoto.selectionPhoto,
          type: "changeEditablePictureElementPicture",
        });
      } else if (draggedPhoto && draggedPhoto.from === "editablePicture") {
        dispatch({
          editablePictureKeyA: elementDefinitionKey,
          editablePictureKeyB: draggedPhoto.editablePictureKey,
          pageKeyA: pageKey,
          pageKeyB: draggedPhoto.pageKey,
          type: "swapEditablePictureElementPicture",
        });
      }

      openSelectionPanel();

      // To be sure and to fix a weird behavior in Safari (dragEnd not called sometimes)
      onDragEnd();
    }
  };

  const onDragStart = () => {
    dispatch({
      draggedPhoto: {
        editablePictureKey: elementDefinitionKey,
        from: "editablePicture",
        pageKey,
      },
      type: "setDraggedPhoto",
    });
  };

  const onDragEnd = () => {
    dispatch({ type: "setDraggedPhoto", draggedPhoto: null });
  };

  const [{ isOver }, drop] = useDrop({
    accept: "draggable",
    drop: () => {
      onDrop();
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const elementSizeMm = sizeP10kToMm(elementDefinition, pageDefinition);
  const elementSizePx = sizeMmToPx(elementSizeMm, ppmm);

  const dropZoneValues = calcDropZone(elementDefinition, pageDefinition, ppmm);

  const pageShapeDefinition = find(
    template.shapeDefinitions,
    ({ key }) => key === pageDefinition.shapeKey
  );
  const pageClippingSVG = pageShapeDefinition ? pageShapeDefinition.svgContent : null;

  const pictureShapeDefinition = find(
    template.shapeDefinitions,
    ({ key }) => key === elementDefinition.shapeKey
  );
  const pictureClippingSVG = pictureShapeDefinition ? pictureShapeDefinition.svgContent : null;
  const clippingId = pictureShapeDefinition
    ? `clipping-${pageDefinition.key}-${elementDefinition.key}`
    : null;

  const styles: React.CSSProperties = {
    height: dropZoneValues.height.value,
    left: dropZoneValues.left.value,
    top: dropZoneValues.top.value,
    width: dropZoneValues.width.value,
  };
  if (clippingId) {
    styles.WebkitClipPath = `url(#${clippingId})`;
    styles.clipPath = `url(#${clippingId})`;
  }

  const clippingBorders = (type: string, position: { left: number; top: number }) => {
    if (!pageClippingSVG) {
      return null;
    }

    return (
      <div
        className={`EditablePicture-${type}-clipping`}
        style={{
          height: dropZoneValues.height.value,
          left: position.left,
          top: position.top,
          width: dropZoneValues.width.value,
        }}
        dangerouslySetInnerHTML={{
          __html: drawClippedPath(elementDefinition, pageDefinition, pageClippingSVG),
        }}
      />
    );
  };

  const isEditPanelTarget =
    panel.key === pageKey && panel.elementDefinitionKey === elementDefinition.key;

  const Wrapper = ({ children, hasPicture }: { children: ReactNode; hasPicture: boolean }) => {
    return (
      <div
        className={classNames("EditablePicture", {
          "EditablePicture--isCandidateToDrop": isCandidateToDrop,
          "EditablePicture--isTargetedByDrag": isCandidateToDrop && isOver,
          "EditablePicture--selected": isEditPanelTarget,
        })}
        onClick={hasPicture ? onClick : noop}
        ref={drop}
      >
        <Draggable
          draggable={hasPicture}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDrop={onDrop}
        >
          {children}
        </Draggable>
        <div className="EditablePicture-dropTarget" style={styles} />
        {!!pageShapeDefinition &&
          clippingBorders("dropTarget", {
            left: dropZoneValues.left.value,
            top: dropZoneValues.top.value,
          })}
        {!pageShapeDefinition && (
          <div
            className={classNames({ "EditablePicture-border--forced": !hasPicture })}
            style={{ margin: `${dropZoneValues.top.value} 0 0 ${dropZoneValues.left.value}` }}
          >
            <ShapeBorders
              elementDefinition={elementDefinition}
              pageDefinition={pageDefinition}
              placementValues={dropZoneValues}
              template={template}
              type="placeholder"
            />
          </div>
        )}
        {pictureClippingSVG && clippingId && (
          <PresentationClipPath id={clippingId} contentSVG={pictureClippingSVG} />
        )}
      </div>
    );
  };

  const element = find(
    page.editablePictures,
    ({ editablePictureKey }) => editablePictureKey === elementDefinition.key
  );
  if (!element) {
    const boxSize = sizeMmToPx(sizeP10kToMm(elementDefinition, pageDefinition), ppmm);
    const { width: boxWidth, height: boxHeight } = boxSize;
    const iconSize =
      boxWidth.value < 150 || boxHeight.value < 150
        ? "small"
        : boxWidth.value > 250 && boxHeight.value > 250
        ? "big"
        : "medium";

    return (
      <Wrapper hasPicture={false}>
        <div
          className="EditablePicture-placeholder"
          style={styles}
          title={intl.formatMessage({
            id: "kustom_EditablePicture_placeholder_info",
          })}
          onClick={(e) => {
            e.stopPropagation();
            if (hasSelectionInPanel(template)) {
              openSelectionPanel(true);
            } else {
              dispatch({
                fixedLayout: true,
                open: true,
                pageKey,
                type: "setGallery",
              });
            }
          }}
        >
          <ImageIcon className={`feather-image EditablePicture-placeholder-icon--${iconSize}`} />
          {!!pageShapeDefinition && clippingBorders("placeholder", { left: 0, top: 0 })}
        </div>
      </Wrapper>
    );
  }

  let shapeSvgContent: string | undefined;
  if (elementDefinition.shapeKey) {
    const shapeDefinition = find(
      template.shapeDefinitions,
      ({ key }) => key === elementDefinition.shapeKey
    );
    if (!shapeDefinition) {
      return <div className="EditablePicture">Error! ShapeDefinition not found.</div>;
    }

    shapeSvgContent = shapeDefinition.svgContent;
  }

  const selectionPhoto = findPhoto(element.selectionPhotoKey, selection);
  const imageUrl = imageUrlFromSelectionPhoto(selectionPhoto, "full");

  return (
    <Wrapper hasPicture={true}>
      <EditablePictureSvg
        cropping={element.cropping}
        filterTag={element.filterTag}
        heightPx={elementSizePx.height.value}
        imageUrl={imageUrl}
        shapeSvgContent={shapeSvgContent}
        widthPx={elementSizePx.width.value}
      />
      <div className="EditablePicture-hover-hidden">
        {isLowQualityPicture(elementSizeMm, selectionPhoto, element.cropping, template.minDpi) && (
          <div className="EditablePicture-lowQuality">
            <LowQualityIndicator />
          </div>
        )}
      </div>
      <div className="EditablePicture-hover-displayed">
        <ShapeBorders
          elementDefinition={elementDefinition}
          pageDefinition={pageDefinition}
          placementValues={dropZoneValues}
          template={template}
          type="resizableHover"
        />
      </div>
      {clippingBorders("dropTarget", {
        left: dropZoneValues.left.value,
        top: dropZoneValues.top.value,
      })}
    </Wrapper>
  );
};

const PresentationClipPath: FunctionComponent<{
  contentSVG: string;
  id: string;
}> = ({ contentSVG, id }) => {
  return (
    <div className="PresentationClipPath">
      <ClippingPath contentSVG={contentSVG} id={id} />
    </div>
  );
};

export default EditablePicture;
